import React, { useCallback, useEffect, useState } from 'react'
import { CRow, CCol, CCard, CCardBody, CButton, CAlert } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCloudUpload, cilPlus, cilCalendar, cilListNumbered, cilTrash } from '@coreui/icons'
import { useNavigate } from 'react-router-dom'
import Table from './Table'
import AddForm from './AddForm'
import EditForm from './EditForm'
import RoundService from 'src/service/RoundService'
import ToastComponent from 'src/components/common/TaostComponent'
import Bootbox from 'bootbox-react'
import StepperBar from '../SetupProgress/StepperBar'

const Round = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [roundNumber, setRoundNumber] = useState()
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState({})
  const [gameType, setGameType] = useState({})
  const [squadFixtureIds, setSquadFixtureIds] = useState([])
  const [addRoundVisible, setAddRoundVisible] = useState(false)
  const [editRoundId, setEditRoundId] = useState(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    try {
      const result = await RoundService.getRound(offset, itemsPerPage, activePage, params)
      setUsers(result.data)
      setRoundNumber(result.rd_number)
      setGameType(result.game_type)
      setSquadFixtureIds(result.getRoundNotInFixture)
    } catch (error) {
      console.error('Error fetching rounds:', error)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnSorter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Delete All confirm box
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([])

  const toggleAllCheckboxes = () => {
    const allIds = squadFixtureIds.map((user) => user.id)
    setSelectedCheckboxes((prevSelected) => (prevSelected.length === allIds.length ? [] : allIds))
  }

  const toggleCheckbox = (id) => {
    setSelectedCheckboxes((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((selectedId) => selectedId !== id)
      } else {
        return [...prevSelected, id]
      }
    })
  }

  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false)
  const handleDeleteAllConfirm = () => {
    RoundService.deleteAllRound({ roundIds: selectedCheckboxes }).then((res) => {
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        setSelectedCheckboxes([])
        fetchUsers()
      } else {
        ToastComponent(res.message, 'error')
      }
    })
    setShowDeleteAllConfirm(false)
  }
  const handleDeleteAllCancel = () => setShowDeleteAllConfirm(false)

  const STAT_CARDS = [
    { label: 'Total Rounds', value: users?.total ?? 0, icon: cilListNumbered, color: 'primary' },
    { label: 'Next Round', value: roundNumber ?? '—', icon: cilCalendar, color: 'info' },
    {
      label: 'Available to Delete',
      value: squadFixtureIds?.length ?? 0,
      icon: cilTrash,
      color: 'secondary',
    },
  ]

  return (
    <>
      <StepperBar activeStepKey="rounds" />
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1">Rounds</h4>
          <div className="text-medium-emphasis">
            Manage rounds, lockout windows and trade settings.
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <CButton color="secondary" variant="outline" onClick={() => navigate('/round-import')}>
            <CIcon icon={cilCloudUpload} className="me-1" />
            Import Rounds
          </CButton>
          <CButton color="primary" onClick={() => setAddRoundVisible(true)}>
            <CIcon icon={cilPlus} className="me-1" />
            Add Round
          </CButton>
        </div>
      </div>

      {gameType == 4 && (
        <CAlert color="danger">Restriction: You can create a maximum of 1 Round only</CAlert>
      )}

      <CRow className="g-3 mb-3">
        {STAT_CARDS.map((s) => (
          <CCol sm={6} lg={4} key={s.label}>
            <CCard className="h-100">
              <CCardBody className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-medium-emphasis small">{s.label}</div>
                  <div className="fs-4 fw-semibold">{s.value}</div>
                </div>
                <div
                  className={`d-flex align-items-center justify-content-center rounded-circle bg-${s.color}-subtle text-${s.color}`}
                  style={{ width: 42, height: 42 }}
                >
                  <CIcon icon={s.icon} size="lg" />
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>

      <CCard>
        <CCardBody>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 bg-light rounded-2">
            <div className="fw-semibold ms-1 me-2">
              {selectedCheckboxes.length} round{selectedCheckboxes.length !== 1 ? 's' : ''} selected
            </div>
            <CButton
              color="danger"
              variant="outline"
              size="sm"
              disabled={selectedCheckboxes.length === 0}
              onClick={() => setShowDeleteAllConfirm(true)}
            >
              <CIcon icon={cilTrash} className="me-1" />
              Delete Selected
            </CButton>
          </div>

          <Table
            users={users}
            fetchUsers={fetchUsers}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
            columnSorter={columnSorter}
            loading={loading}
            selectedCheckboxes={selectedCheckboxes}
            squadFixtureIds={squadFixtureIds}
            toggleAllCheckboxes={toggleAllCheckboxes}
            toggleCheckbox={toggleCheckbox}
            onEditRound={setEditRoundId}
          />
        </CCardBody>
      </CCard>

      <AddForm
        visible={addRoundVisible}
        onClose={() => setAddRoundVisible(false)}
        roundNumber={roundNumber}
        fetchUsers={fetchUsers}
      />

      <EditForm
        roundId={editRoundId}
        visible={!!editRoundId}
        onClose={() => setEditRoundId(null)}
        fetchUsers={fetchUsers}
      />

      <Bootbox
        show={showDeleteAllConfirm}
        type={'confirm'}
        message={'Are you sure you want to delete all selected rounds?'}
        onSuccess={handleDeleteAllConfirm}
        onCancel={handleDeleteAllCancel}
        onClose={handleDeleteAllCancel}
      />
    </>
  )
}

export default Round
